import { useEffect, useState } from "react";
import { Star, X, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import REVIEWS from "@/data/reviews";
import { useCity } from "@/context/LocationContext";

const Stars = ({ n }) => (
    <div className="flex items-center gap-px">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                                key={`star-${i}`}
                                className={`h-2.5 w-2.5 ${i < n ? "fill-[#FFB800] text-[#FFB800]" : "text-zinc-300"}`}
                                strokeWidth={1.5}
                            />
                        ))}
    </div>
);

const ReviewsWidget = () => {
    const { city, postal } = useCity();
    const cityLabel = city ? `${city}${postal ? " " + postal : ""}` : "";
    const [index, setIndex] = useState(0);
    const [collapsed, setCollapsed] = useState(false);
    const [pulse, setPulse] = useState(true);

    useEffect(() => {
        if (collapsed) return;
        const t = setInterval(() => setIndex((i) => (i + 1) % REVIEWS.length), 5000);
        return () => clearInterval(t);
    }, [collapsed]);

    useEffect(() => {
        const t = setTimeout(() => setPulse(false), 12000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const r = REVIEWS[index];

    if (collapsed) {
        return (
            <button
                type="button"
                onClick={() => setCollapsed(false)}
                data-testid="reviews-widget-open"
                aria-label="Afficher les avis clients"
                className="fixed bottom-3 left-3 z-40 bg-white border border-black/10 hover:border-black shadow-md rounded-full px-2 py-1 flex items-center gap-1 transition-colors"
            >
                <Stars n={5} />
                <span className="text-[9px] font-display font-bold tracking-tight uppercase">Avis</span>
            </button>
        );
    }

    return (
        <div
            data-testid="reviews-widget"
            className={`fixed bottom-3 left-3 z-40 w-[180px] bg-white border border-black/10 shadow-md ${pulse ? "reviews-pulse" : ""}`}
        >
            <div className="flex items-center justify-between px-2 py-1 border-b border-black/10 bg-[#FAFAFA]">
                <div className="flex items-center gap-1">
                    <span className="relative flex h-1 w-1">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#E63946] opacity-75 animate-ping"></span>
                        <span className="relative inline-flex rounded-full h-1 w-1 bg-[#E63946]"></span>
                    </span>
                    <span className="text-[8px] uppercase tracking-[0.12em] font-semibold text-zinc-700">Avis</span>
                </div>
                <div className="flex items-center">
                    <button type="button" onClick={() => setIndex((i) => (i - 1 + REVIEWS.length) % REVIEWS.length)} aria-label="Précédent" className="p-0.5 hover:bg-zinc-100" data-testid="reviews-prev"><ChevronLeft className="h-2.5 w-2.5" /></button>
                    <button type="button" onClick={() => setIndex((i) => (i + 1) % REVIEWS.length)} aria-label="Suivant" className="p-0.5 hover:bg-zinc-100" data-testid="reviews-next"><ChevronRight className="h-2.5 w-2.5" /></button>
                    <button type="button" onClick={() => setCollapsed(true)} aria-label="Réduire" className="p-0.5 hover:bg-zinc-100" data-testid="reviews-close"><X className="h-2.5 w-2.5" /></button>
                </div>
            </div>

            <div className="p-2 review-fade" key={index}>
                <div className="flex items-center justify-between">
                    <Stars n={r.rating} />
                    <span className="text-[7px] uppercase tracking-[0.1em] font-semibold text-zinc-500 truncate max-w-[80px]">{r.service}</span>
                </div>
                <Quote className="h-2.5 w-2.5 text-[#E63946] mt-1.5" strokeWidth={2.4} />
                <p className="mt-0.5 text-[10px] text-zinc-700 leading-snug line-clamp-3">{r.text}</p>
                <div className="mt-1.5 pt-1.5 border-t border-black/10 flex items-center justify-between">
                    <div>
                        <div className="text-[9px] font-display font-bold leading-tight">{r.name}</div>
                        {cityLabel && (
                            <div className="text-[7px] uppercase tracking-[0.1em] text-zinc-500">{cityLabel}</div>
                        )}
                    </div>
                    <div className="flex items-center gap-px">
                        {REVIEWS.map((rev, i) => (
                            <span key={rev.name} className={`h-0.5 transition-all ${i === index ? "w-2 bg-[#E63946]" : "w-0.5 bg-zinc-300"}`} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReviewsWidget;
